import * as React from "react";
import * as _ from "lodash";
import { useRouteMatch } from "react-router-dom";
import { GroupBase, OnChangeValue, StylesConfig, components, OptionProps } from "react-select";
import { SelectComponents } from "react-select/dist/declarations/src/components";
import styled, { css } from "styled-components";

import Select from "./select/Select";
import { IPathParams } from "navigation/App";
import { AgentGroup } from "shared/model/AgentGroup";
import { HashMap } from "shared/model/HashMap";
import { Option, FixedOption } from "shared/model/Option";
import { Utilities } from "shared/utilities";
import colors from "shared/styles/colors";
import useUserTheme from "shared/styles/hooks/useUserTheme";
import { ThemeType } from "shared/styles/theme";
import { removePageFromSearchParameters } from "processList/model/AppPath";

const { Option: ReactSelectMenuOption, SingleValue: ReactSelectSingleValue } = components;

const BaseStyledOption = css<Option>`
    display: flex;
    align-items: center;
    width: 100%;
    font-family: 'Gilroy-Medium', 'medium-fallback';
    font-size: 14px;
    line-height: 16px;
    color: ${(props) => props.$textColor ? props.$textColor : 'inherit'};
`;

const StyledOptionA = styled.a<Partial<Option>>`
    ${BaseStyledOption}
`;

const StyledOptionDiv = styled.div<Partial<Option>>`
    ${BaseStyledOption}
`;

const StyledIconWrapper = styled.i<Partial<Option>>`
    font-size: 1.5rem;
    margin-right: 14px;

    &::before {
        color: ${props => props.$isSelected || props.$isDisabled
        ? `${props.$iconColor ? props.$iconColor : 'currentColor'}`
        : 'transparent'};
    }
`;

const CommonAgentGroupSelectMenuOption: React.FC<any> = (props) => {
    const { children, ...rest } = props;

    return <ReactSelectMenuOption {...rest}>{children}</ReactSelectMenuOption>;
}

const AgentGroupSelectMenuOption: React.FC<IAgentGroupSelectMenuOptionProps> = (props) => {
    const { data, disabledTitleText, ...rest } = props;
    const [theme] = useUserTheme();

    const $textColor = theme === ThemeType.light ? colors.black : colors.grey_5
    const $iconColor = theme === ThemeType.light ? colors.purplePrimary : colors.purpleSecondary

    return (
        <CommonAgentGroupSelectMenuOption
            {...rest}
        >
            <StyledOptionDiv
                $iconColor={$iconColor}
                $textColor={$textColor}
                $isSelected={props.isSelected}
                $isDisabled={props.isDisabled}
                title={data.$isDisabled && disabledTitleText ? disabledTitleText : undefined}>
                {data.$isDisabled ? (
                    <StyledIconWrapper $iconColor={$iconColor} $isSelected={props.isSelected} $isDisabled={props.isDisabled} className="ci-warning_outline" />
                ) : (
                    <StyledIconWrapper $iconColor={$iconColor} $isSelected={props.isSelected} $isDisabled={props.isDisabled} className="ci-check_big" />
                )}
                {data.label}
            </StyledOptionDiv>
        </CommonAgentGroupSelectMenuOption>
    );
}

const AgentGroupSelectMenuLinkOption: React.FC<any> = (props) => {
    const { data, ...rest } = props;
    const [theme] = useUserTheme();

    const $textColor = theme === ThemeType.light ? colors.black : colors.grey_5
    const $iconColor = theme === ThemeType.light ? colors.purplePrimary : colors.purpleSecondary

    return (
        <CommonAgentGroupSelectMenuOption {...props}>
            <StyledOptionA
                $iconColor={$iconColor}
                $textColor={$textColor}
                href={data.linkHref}
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => e.preventDefault()}
                tabIndex={-1}>
                <StyledIconWrapper $iconColor={$iconColor} $isSelected={props.isSelected} $isDisabled={props.isDisabled} className="ci-check_big" />
                {data.label}
            </StyledOptionA>
        </CommonAgentGroupSelectMenuOption>
    );
}

const AgentGroupSelectMenu: React.FC<IAgentGroupSelectMenuProps> = (props) => {
    const [theme] = useUserTheme();
    const selectStyles: StylesConfig<FixedOption> = {
        control: (provided) => ({
            ...provided,
            borderRadius: '25px',
            border: `2px solid ${colors.grey_2}`,
            minWidth: '269px',
            height: '36px',
            fontSize: '18px',
            boxShadow: 'none',
            color: theme === ThemeType.light ? colors.black : colors.white,
            backgroundColor: theme === ThemeType.light ? colors.white : colors.dark2,
            "&:hover": {
                borderColor: colors.grey_2
            }
        }),
        menu: (provided) => ({
            ...provided,
            borderRadius: "8px",
            marginTop: 16,
            marginRight: -16,
            width: "max-content",
            minWidth: "100%",
            boxShadow: "0px 8px 8px rgba(0, 0, 0, 0.1)",
            border: `1px solid ${colors.grey_2}`,
            color: theme === ThemeType.light ? colors.black : colors.white,
            backgroundColor: theme === ThemeType.light ? colors.white : colors.dark2
        }),
        option: (provided) => ({
            ...provided,
            alignItems: "center",
            backgroundColor: theme === ThemeType.light ? colors.white : colors.dark2,
            padding: "2px 48px 2px 12px",
            "&:hover": {
                backgroundColor: theme === ThemeType.light ? colors.grey_5 : colors.dark3
            }
        }),
    };

    return <AgentGroupSelectMenuBase
        {...props}
        selectStyles={selectStyles}
        components={{ Option: (optionProps) => AgentGroupSelectMenuOption({ ...optionProps, disabledTitleText: props.disabledTitleText }) }}
    />;
}

const TitleAgentGroupSelectSingleValue: React.FC<any> = (props) => {
    const { data, ...rest } = props;
    return (
        <ReactSelectSingleValue {...rest}>
            {data.title}
        </ReactSelectSingleValue>
    );
}

export const TitleAgentGroupSelectMenu: React.FC<ITitleAgentGroupSelectMenuProps> = (props) => {
    const [theme] = useUserTheme();
    const route = `${Utilities.getBaseUrlPath(props.isBasePathMatched ? props.basePath : '')}/:environmentName?/:agentGroupName?`;
    const match = useRouteMatch<IPathParams>(route);
    const [searchQuery, setSearchQuery] = React.useState("");

    React.useEffect(() => {
        // remove page from search query links
        if (props.searchQuery) {
            const search = removePageFromSearchParameters(props.searchQuery);
            setSearchQuery(search);
        }
    }, [props.searchQuery]);

    const environmentName = match?.params.environmentName || 'Default';
    const agentGroupName = match?.params.agentGroupName || 'Default';

    const getCurrentAgentGroupName = () => {
        if (!props.agentGroups) {
            return agentGroupName;
        }

        const doesAgentGroupAlreadyExist = props.agentGroups.some((g) => g.name.toLowerCase() === agentGroupName.toLowerCase());
        if (doesAgentGroupAlreadyExist) {
            return agentGroupName;
        }

        return props.agentGroups.find((g) => g.environment.toLowerCase() === environmentName.toLowerCase())?.name || agentGroupName;
    }

    const getAgentGroupPath = (environmentName: string, agentGroupName: string) => {
        return `${Utilities.getBaseUrlPath(props.basePath)}/${environmentName}/${agentGroupName}${props.path ? `/${props.path}` : ''}${searchQuery}`;
    }

    const selectStyles: StylesConfig<FixedOption> = {
        control: (provided, { isFocused }) => ({
            ...provided,
            border: `2px solid ${theme === ThemeType.light ? colors.purpleBackground : colors.dark2}`,
            borderRadius: "20px",
            width: 'fit-content',
            height: '36px',
            fontSize: '18px',
            boxShadow: 'none',
            paddingLeft: "10px",
            backgroundColor: theme === ThemeType.light ? colors.white : colors.dark,
            ...isFocused && ({
                borderColor: theme === ThemeType.light ? colors.purpleBackground : colors.dark2,
            }),
            ":hover": {
                ...provided[":hover"],
                borderColor: theme === ThemeType.light ? colors.purpleBackground : colors.dark2,
            }
        }),
        menu: (provided) => ({
            ...provided,
            borderRadius: "8px",
            marginTop: 8,
            marginRight: -16,
            width: "max-content",
            minWidth: "100%",
            boxShadow: "0px 8px 8px rgba(0, 0, 0, 0.1)",
            border: `1px solid ${colors.grey_2}`,
            color: theme === ThemeType.light ? colors.black : colors.grey_5,
            backgroundColor: theme === ThemeType.light ? colors.white : colors.dark2
        }),
        menuList: (provided) => ({
            ...provided,
            padding: "8px 0",
        }),
        group: (provided) => ({
            ...provided,
            padding: 0,
        }),
        groupHeading: (provided) => ({
            ...provided,
            letterSpacing: "2px",
            color: theme === ThemeType.light ? colors.grey_1 : colors.grey_2,
            fontFamily: "'Gilroy-Medium', 'medium-fallback'",
            margin: 0,
            padding: '6px 48px 6px 16px',
            fontSize: 14,
            lineHeight: '14px',
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: '0',
            paddingBottom: '6px',
        }),
        singleValue: (provided) => ({
            ...provided,
            fontSize: '18px',
            lineHeight: '21px',
            color: theme === ThemeType.light ? colors.grey_1 : colors.grey_5,
            fontFamily: "Gilroy-Regular",
            fontStyle: "normal",
            fontWeight: 400
        }),
        option: (provided) => ({
            ...provided,
            alignItems: "center",
            backgroundColor: theme === ThemeType.light ? colors.white : colors.dark2,
            padding: "2px 48px 2px 12px",
            "&:hover": {
                backgroundColor: theme === ThemeType.light ? colors.grey_5 : colors.dark3
            }
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            color: theme === ThemeType.light ? colors.black : colors.grey_5
        }),
        input: (provided) => ({
            ...provided,
            color: theme === ThemeType.light ? colors.black : colors.grey_5
        })
    };

    return (
        <AgentGroupSelectMenuBase
            agentGroups={props.agentGroups}
            currentAgentGroupName={getCurrentAgentGroupName()}
            onChangeAgentGroup={props.onChangeAgentGroup}
            selectStyles={selectStyles}
            components={{
                SingleValue: TitleAgentGroupSelectSingleValue,
                Option: AgentGroupSelectMenuLinkOption,
            }}
            getOptionLinkHref={getAgentGroupPath}
        />
    )
}

interface IAgentGroupSelectMenuBaseProps extends IAgentGroupSelectMenuProps {
    selectStyles: StylesConfig<FixedOption>,
    components?: Partial<SelectComponents<Option<any>, false, GroupBase<Option<any>>>>,
    getOptionLinkHref?: (environmentName: string, agentGroupName: string) => string;
}

const AgentGroupSelectMenuBase: React.FC<IAgentGroupSelectMenuBaseProps> = (props) => {

    // Do not show the agent group selector before we have the data needed.
    if (!props.agentGroups || !props.agentGroups.length) {
        return null;
    }

    const handleChangeAgentGroup = (newValue: OnChangeValue<Option, false>) => {
        const selectedAgentGroupName: string = newValue?.value;
        // let the user reselect the same agent group, this prevents a missing agent group from preventing selecting default ACC-6736
        const agentGroup = props.agentGroups.find((ag) => ag.name === selectedAgentGroupName);
        const environmentName = agentGroup && agentGroup.environment || "";
        props.onChangeAgentGroup(environmentName, selectedAgentGroupName);
    }

    const getAgentGroups = () => {
        const agentGroupsByEnvironmentGroups: GroupBase<Option>[] = [];
        const agentGroupsByEnvironment: { [environmentName: string]: AgentGroup[] } = _.groupBy(props.agentGroups, (ag) => ag.environment);

        const getAgentGroupsForEnv = (agentGroups: AgentGroup[]) => {
            const returnableAgentGroups: Option[] = []
            for (const ag of agentGroups) {
                const isDisabled = props.disabledAgentGroupNames && props.disabledAgentGroupNames.indexOf(ag.name) !== -1;
                returnableAgentGroups.push({
                    label: ag.displayName,
                    value: ag.name,
                    $isDisabled: isDisabled,
                    $isSelected: props.currentAgentGroupName === ag.name,
                    title: `${ag.environmentDisplayName} / ${ag.displayName}`,
                    linkHref: props.getOptionLinkHref ? props.getOptionLinkHref(ag.environment, ag.name) : undefined,
                })
            }
            return returnableAgentGroups;
        }

        for (const envName of Object.keys(agentGroupsByEnvironment)) {
            const agentGroups: AgentGroup[] = agentGroupsByEnvironment[envName];
            agentGroupsByEnvironmentGroups.push({
                label: `${agentGroups.length > 0 ? agentGroups[0].environmentDisplayName : envName}`,
                options: getAgentGroupsForEnv(agentGroups)
            })
        }
        return agentGroupsByEnvironmentGroups;
    }

    const agentGroups = getAgentGroups();

    return (
        <Select
            classNamePrefix="react-select"
            className="agent-group-selector"
            options={agentGroups}
            value={Utilities.valueInGroupFromId(agentGroups, props.currentAgentGroupName)}
            styles={props.selectStyles}
            onChange={handleChangeAgentGroup}
            menuPortalTarget={document.body}
            components={props.components}
            isAgentGroupSelectorWithLinks
        />
    )
}

interface IAgentGroupSelectMenuOptionProps extends OptionProps<Option<any>, false, GroupBase<Option<any>>> {
    disabledTitleText?: string
}

interface ITitleAgentGroupSelectMenuProps {
    basePath: string;
    agentGroups: AgentGroup[];
    isBasePathMatched?: boolean;
    path?: string;
    searchQuery?: URLSearchParams;
    onChangeAgentGroup: (environmentName: string, agentGroupName: string) => void;
}

export interface IAgentGroupData {
    agentGroupHash: HashMap<AgentGroup>;
    agentGroupList: AgentGroup[]
}

export interface IAgentGroupSelectMenuProps {
    currentAgentGroupName: string;
    agentGroups: AgentGroup[];
    disabledAgentGroupNames?: string[];
    disabledTitleText?: string;
    showManageLink?: boolean;
    baseUri?: string;
    onChangeAgentGroup: (environmentName: string, agentGroupName: string) => void;
}

export default AgentGroupSelectMenu;